import { apiFetch } from "./apiClient";
import { saveAssignmentAsset } from "./knowledgeStore";
import { extractAudio } from "./audioExtract";

/**
 * Assignment tab service.
 *
 * Channels are deliberately in-memory (see the migration note): each ad is its
 * own isolated conversation, so nothing accumulates across ads and nothing is
 * written to the DB. Only the shared master prompt and the org's API keys live
 * server-side.
 */

let workspaceId = null;
export const setAssignmentWorkspaceId = (id) => {
  workspaceId = id || null;
};

const withWorkspace = (path) => {
  const sep = path.includes("?") ? "&" : "?";
  return workspaceId
    ? `${path}${sep}workspace_id=${encodeURIComponent(workspaceId)}`
    : path;
};

// ---------------------------------------------------------------------------
// Org prompts (shared across the whole team)
// ---------------------------------------------------------------------------

export const fetchOrgPrompts = () => apiFetch("/api/org/prompts");

export const saveOrgPrompt = (key, value) =>
  apiFetch("/api/org/prompts", { method: "PUT", body: { key, value } });

export const resetOrgPrompt = (key) =>
  apiFetch("/api/org/prompts", { method: "PUT", body: { key, reset: true } });

// ---------------------------------------------------------------------------
// Team + permissions
// ---------------------------------------------------------------------------

export const fetchOrgUsers = () => apiFetch("/api/org/users");

export const updateOrgUser = ({ id, features, tabs, isAdmin, action }) =>
  apiFetch("/api/org/users", {
    method: "PATCH",
    body: {
      id,
      ...(features ? { features } : {}),
      ...(tabs ? { tabs } : {}),
      ...(isAdmin === undefined ? {} : { is_admin: isAdmin }),
      ...(action ? { action } : {}),
    },
  });

/** approve | deny | revoke */
export const setUserAccess = (id, action) => updateOrgUser({ id, action });

// ---------------------------------------------------------------------------
// Invites — an invited email is auto-approved on first login
// ---------------------------------------------------------------------------

export const fetchOrgInvites = () => apiFetch("/api/org/invites");

export const createOrgInvite = ({ email, features, note }) =>
  apiFetch("/api/org/invites", {
    method: "POST",
    body: { email, features, note },
  });

export const deleteOrgInvite = (email) =>
  apiFetch("/api/org/invites", { method: "DELETE", body: { email } });

// ---------------------------------------------------------------------------
// Media prep
// ---------------------------------------------------------------------------

export const isVideoFile = (file) =>
  String(file?.type || "").toLowerCase().startsWith("video");

const MAX_IMAGE_EDGE = 2000;

/**
 * Downscale a static ad to a sane edge length and return base64 JPEG.
 * Ad creatives carry text, so we keep this generous — but not so generous that
 * a single image burns ~5k tokens.
 */
export const fileToImagePart = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      try {
        const scale = Math.min(
          1,
          MAX_IMAGE_EDGE / Math.max(img.width, img.height),
        );
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        // Flatten transparency onto white — a PNG ad with alpha otherwise reads
        // as black once JPEG-encoded.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        resolve({
          mime: "image/jpeg",
          b64: dataUrl.split(",")[1],
          previewUrl: dataUrl,
        });
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(url);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };

    img.src = url;
  });

// ---------------------------------------------------------------------------
// Pipeline
// ---------------------------------------------------------------------------

/**
 * Get the video's audio to storage — the ONLY thing the transcriber needs.
 *
 * Normal path: strip the audio in the browser and upload a few MB of MP3 instead
 * of the whole video. A 200 MB video becomes ~5 MB, which uploads in seconds and
 * stays well under Supabase's default 50 MB object limit — so no storage limit
 * has to be raised, however long the video is.
 *
 * Fallback: if ffmpeg.wasm can't handle the file (exotic codec, or a file too
 * big for the WASM heap), upload the video itself and let the GPU worker demux
 * it. That path DOES depend on the storage object limit.
 */
export const uploadForTranscription = async ({ id, file, onProgress }) => {
  try {
    const audio = await extractAudio(file, { onProgress });
    const asset = await saveAssignmentAsset({
      id: `${id}-audio`,
      name: `${file.name}.mp3`,
      blob: audio.blob,
      mimeType: audio.mimeType,
    });
    return { ...asset, mode: "audio", bytes: audio.blob.size };
  } catch (error) {
    // Fall back to shipping the video itself.
    const asset = await saveAssignmentAsset({
      id,
      name: file.name,
      blob: file,
      mimeType: file.type || "video/mp4",
    });
    return {
      ...asset,
      mode: "video",
      bytes: file.size,
      fallbackReason: String(error?.message || error),
    };
  }
};

/**
 * Modal / faster-whisper: verbatim transcript + timed segments.
 *
 * Job-based so LONG media works: the server spawns the GPU job and returns a
 * job_id instantly; we poll until it finishes. No HTTP connection ever outlives
 * a moment, so nothing times out no matter how long the video is.
 */
export const transcribeMedia = async ({ storagePath }) => {
  const start = await apiFetch(withWorkspace("/api/ai/transcribe"), {
    method: "POST",
    body: { workspace_id: workspaceId, storage_path: storagePath },
  });

  const jobId = start?.job_id;
  if (!jobId) {
    // A legacy synchronous server returns the result directly.
    if (start?.transcript !== undefined) return start;
    throw new Error(start?.error || "Transcription failed to start.");
  }

  const deadline = Date.now() + 45 * 60 * 1000; // hard client-side cap
  let delay = 4000;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(12_000, delay + 1000); // ease off as the job runs long

    const poll = await apiFetch(withWorkspace("/api/ai/transcribe"), {
      method: "POST",
      body: { workspace_id: workspaceId, job_id: jobId },
    }).catch(() => null); // one flaky poll shouldn't kill a 20-minute job

    if (!poll) continue;
    if (poll.status === "done") return poll.result;
    if (poll.status === "error") {
      throw new Error(poll.error || "Transcription failed.");
    }
  }

  throw new Error("Transcription timed out after 45 minutes.");
};

/**
 * Retry a call when the server throttles it (429). Bulk runs can momentarily
 * exceed the per-minute cap; rather than fail an ad, we wait out the window the
 * server tells us to (Retry-After) and try again. This keeps the rate limit as
 * an overload guard without breaking a legitimate large batch.
 */
const withRateLimitRetry = async (fn, { tries = 4 } = {}) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      const status = error?.status;
      if (status !== 429 || attempt >= tries - 1) throw error;
      const retryAfter = Number(error?.payload?.retry_after);
      const waitMs = Number.isFinite(retryAfter)
        ? retryAfter * 1000
        : Math.min(30_000, 1000 * 2 ** attempt);
      await new Promise((r) => setTimeout(r, waitMs));
    }
  }
};

export const analyzeAd = ({
  mediaKind,
  filename,
  images,
  transcript,
  segments,
  bulk = false,
}) =>
  withRateLimitRetry(() =>
    apiFetch(withWorkspace("/api/assignment/analyze"), {
      method: "POST",
      body: {
        workspace_id: workspaceId,
        mode: "analyze",
        media_kind: mediaKind,
        filename,
        images,
        transcript,
        segments,
        bulk,
      },
    }),
  );

export const chatWithAd = ({
  mediaKind,
  filename,
  images,
  transcript,
  segments,
  history,
  message,
  priorResult,
}) =>
  apiFetch(withWorkspace("/api/assignment/analyze"), {
    method: "POST",
    body: {
      workspace_id: workspaceId,
      mode: "chat",
      media_kind: mediaKind,
      filename,
      images,
      transcript,
      segments,
      history,
      message,
      // So the model can defend the copy it actually assigned.
      prior_result: priorResult || null,
    },
  });

/**
 * Run `tasks` with bounded concurrency.
 *
 * Bulk runs go wide, but not unbounded: too many parallel Anthropic calls will
 * trip rate limits, and — more subtly — the first request must land before the
 * others so they can READ the cached master prompt instead of each writing their
 * own copy of it. So we prime with a single task, then fan out.
 */
export const runPool = async (tasks, { concurrency = 4, prime = true } = {}) => {
  const queue = [...tasks];
  if (!queue.length) return;

  if (prime) {
    const first = queue.shift();
    await first();
  }

  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length) {
      const task = queue.shift();
      if (!task) return;
      await task();
    }
  });

  await Promise.all(workers);
};
